// Native chat Spec 01 — Host Controls chat panel (wireframe D).
// Same feed as the viewer, plus: hover moderation on NATIVE rows only (Delete /
// Mute) — relayed external rows get neither (that lane's tool is the SSN
// allowlist). Deleted messages show a tombstone here (keepDeleted) while viewers
// see them vanish. A muted-list card (host RLS read) offers Unmute. Compose posts
// through the same server-verified path; the server stamps the role.

import { useRoomContext } from '@livekit/components-react';
import { useCallback, useEffect, useState } from 'react';

import { ChatCompose, ChatFeed } from './SocialChatPanel';
import { useSocialChat, type FeedChat } from './useSocialChat';

import { useAuth } from '@/lib/auth/useAuth';
import { supabase } from '@/lib/supabase';

interface MutedUser {
  userId: string;
  name: string;
}

export function LiveChatHostPanel({
  roomId,
  role,
}: {
  /** rooms.id (uuid) — for the host-only muted-list read. */
  roomId: string;
  role: 'host' | 'cohost';
}) {
  const room = useRoomContext();
  const { profile } = useAuth();
  const chat = useSocialChat(room, { keepDeleted: true });
  const [mutedList, setMutedList] = useState<MutedUser[]>([]);

  const refreshMutes = useCallback(async () => {
    const { data: rows } = await supabase
      .from('live_chat_mutes')
      .select('user_id')
      .eq('room_id', roomId);
    if (!rows || rows.length === 0) {
      setMutedList([]);
      return;
    }
    const ids = rows.map((r) => r.user_id);
    const { data: profs } = await supabase
      .from('profiles')
      .select('id, display_name, username')
      .in('id', ids);
    const nameById = new Map(
      (profs ?? []).map((p) => [p.id, p.display_name ?? p.username ?? 'Member']),
    );
    setMutedList(ids.map((id) => ({ userId: id, name: nameById.get(id) ?? 'Member' })));
  }, [roomId]);

  useEffect(() => {
    void refreshMutes();
  }, [refreshMutes]);

  const onDelete = useCallback(
    (messageId: string) => void chat.moderate('delete', { messageId }).catch(() => {}),
    [chat],
  );
  const onMute = useCallback(
    (userId: string) =>
      void chat
        .moderate('mute', { userId })
        .then(refreshMutes)
        .catch(() => {}),
    [chat, refreshMutes],
  );
  const onUnmute = useCallback(
    (userId: string) =>
      void chat
        .moderate('unmute', { userId })
        .then(refreshMutes)
        .catch(() => {}),
    [chat, refreshMutes],
  );

  const renderChatRow = useCallback(
    (m: FeedChat) => {
      if (m.deleted) {
        return <li className="font-sans text-xs italic text-video-text-faint">Message removed</li>;
      }
      const native = m.platform === 'archetelos';
      const isHost = m.role === 'host' || m.role === 'cohost';
      return (
        <li className="group flex items-start gap-2">
          <span className="min-w-0 flex-1 font-sans text-sm leading-snug">
            <span
              className={isHost ? 'font-medium text-oxblood-live' : 'font-medium text-video-text'}
            >
              {m.author.name}
            </span>
            {isHost && m.role ? (
              <span className="ml-1.5 rounded-sm bg-oxblood px-1.5 py-px align-middle text-[8px] font-semibold uppercase tracking-[0.1em] text-white">
                {m.role}
              </span>
            ) : null}{' '}
            <span className="text-video-text-muted">{m.text}</span>
          </span>
          {native && (
            <span className="flex shrink-0 gap-1 opacity-0 transition group-hover:opacity-100">
              <button
                type="button"
                onClick={() => onDelete(m.id)}
                className="rounded-sm border border-oxblood-live/50 px-1.5 py-0.5 font-sans text-[9px] text-oxblood-live"
              >
                Delete
              </button>
              {m.senderId && !isHost && (
                <button
                  type="button"
                  onClick={() => onMute(m.senderId!)}
                  className="rounded-sm border border-video-line px-1.5 py-0.5 font-sans text-[9px] text-video-text-muted"
                >
                  Mute
                </button>
              )}
            </span>
          )}
        </li>
      );
    },
    [onDelete, onMute],
  );

  return (
    <div className="flex h-full flex-col">
      <p className="font-sans text-xs font-medium uppercase tracking-[0.12em] text-video-text-faint">
        Live chat — host view
      </p>
      <div className="mt-2 flex min-h-0 flex-1 flex-col rounded-md border border-video-line bg-video-inset">
        <ChatFeed
          messages={chat.messages}
          backfillCount={chat.backfillCount}
          renderChatRow={renderChatRow}
        />
        {mutedList.length > 0 && (
          <div className="mx-3 mb-2 rounded-md border border-video-line px-2.5 py-2 font-sans text-[10px] text-video-text-muted">
            <span className="text-video-text-faint">Muted (this stream): </span>
            {mutedList.map((u, i) => (
              <span key={u.userId}>
                {i > 0 && ', '}
                <b className="text-video-text">{u.name}</b>
                <button
                  type="button"
                  onClick={() => onUnmute(u.userId)}
                  className="ml-1 text-oxblood-live underline"
                >
                  Unmute
                </button>
              </span>
            ))}
          </div>
        )}
        <ChatCompose
          onSend={chat.send}
          muted={chat.muted}
          cooldownUntil={chat.cooldownUntil}
          label={`Posting as ${profile?.display_name ?? profile?.username ?? 'Host'} · ${role.toUpperCase()}`}
        />
      </div>
    </div>
  );
}
